import { Injectable } from '@angular/core';
import { Observable, Subject, forkJoin, map, of, switchMap } from 'rxjs';
import { HTTPService } from 'src/app/services/http.service';
import { UserAuthenticationServices } from 'src/app/services/user-authentication.services';
import { StringUtils } from 'src/app/system-utils/utils/StringUtils';
import {
    CHAMADOS_DB_NAME,
    COLLECTION_AGENTS,
    COLLECTION_CALENDARS,
    COLLECTION_CATEGORIES,
    COLLECTION_CHANNELS,
    COLLECTION_CONFIG,
    COLLECTION_CONTACTS,
    COLLECTION_MACROS,
    COLLECTION_MESSAGES,
    COLLECTION_QUEUES,
    COLLECTION_TICKETS,
    Ticket,
    TicketAgent,
    TicketAttachment,
    TicketCalendar,
    TicketCategory,
    TicketChannel,
    TicketChannelConfig,
    TicketConfig,
    TicketContact,
    TicketMacro,
    TicketMessage,
    TicketPriority,
    TicketQueue,
    TicketQueueMember,
    TicketStatus,
} from '../models/chamados.models';

/** Resultado paginado do findnative no caminho Mongo. */
export interface PagedResult<T> {
    rows: T[];
    total: number;
}

/**
 * Usuario elegivel a ser membro de fila.
 *
 * Vem do SQL SERVER (MKTPUSER), nao do Mongo: o cadastro de usuario e do
 * sistema inteiro e continua onde sempre esteve. A fila guarda apenas o
 * iduser e o nome, copiados no momento da escolha.
 */
export interface CompanyUser {
    IDUSER: number;
    USERNAME: string;
}

/**
 * Agente do agent-builder, oferecido como bot de triagem.
 *
 * Vem da colecao `Mktpagents`, que fica no banco Mongo PADRAO — nao no banco
 * dedicado dos chamados. Por isso a consulta dele nao leva `dbName`.
 */
export interface BotAgent {
    _id: string;
    agentName: string;
}

/**
 * Fluxo TemAPI, oferecido como caminho de envio de um canal.
 *
 * Vem do SQL SERVER (`MKTPCFG`) — o cadastro de fluxo e do sistema inteiro e
 * continua onde sempre esteve.
 */
export interface FlowOption {
    IDCONF: number;
    NAME: string;
}

/**
 * Ordenacoes oferecidas na mesa.
 *
 * `PRIORIDADE` e o caso especial — ver `listTickets`.
 */
export type TicketSortField =
    | 'ULTIMA_MENSAGEM'
    | 'ABERTURA'
    | 'PRAZO_SLA'
    | 'PRIORIDADE';

export interface TicketSort {
    field: TicketSortField;
    /** true = mais recente / mais urgente primeiro. */
    desc: boolean;
}

/**
 * Dados de abertura de chamado pelo app.
 *
 * `codecompany`, `codecontract` e `iduser` NAO entram aqui: eles vem da sessao
 * e sao acrescentados pelo servico. Quem chama nao escolhe em nome de quem
 * abre.
 */
export interface AbrirChamadoRequest {
    /**
     * ONDE o chamado esta sendo aberto: o contrato da CONFIGURACAO (a central
     * de atendimento). E ele que traz fila, categoria, SLA e prazo.
     */
    codecontract?: number;
    /**
     * DE QUEM e o chamado: o contrato do CLIENTE.
     *
     * Quase nunca e o mesmo do `codecontract` — quem abre pertence ao contrato
     * dele, e a central e de outro. E o campo pelo qual a mesa filtra.
     */
    codecontractConfiguracao?: number;
    /** Unidade do cliente, quando a fila pede. */
    codecompany?: number;
    /** O chamado que este continua, quando vem de um encerrado. */
    ticketPaiId?: string;
    ticketPaiNumero?: string;
    categoriaId?: string;
    assunto: string;
    descricao: string;
    prioridade?: string;
    camposExtra?: Record<string, unknown>;
    /** Ja subidos: o `ref` e a URL que a conversa transforma em link. */
    anexos?: TicketAttachment[];
}

/** Filtros da mesa de atendimento. Tudo opcional: a mesa abre sem filtro. */
export interface TicketListFilter {
    status?: TicketStatus[];
    filaId?: string;
    responsavelIduser?: number;
    canal?: string;
    codecompany?: number;
    /** Busca no indice de texto (assunto + descricao). */
    texto?: string;
    /** Contrato do chamado — de qual cliente ele e. */
    codecontract?: number;
    /** Contrato da configuracao que originou o chamado. */
    codecontractConfiguracao?: number;
    /**
     * UM chamado, pelo numero visivel (`TKT-1042`).
     *
     * E o que a notificacao do sino usa para posicionar a tela: ela manda o
     * numero, a lista volta com um item so e a tela o seleciona sozinha.
     */
    numero?: string;
    /**
     * Recorte de acesso do atendente logado, ja em formato de filtro Mongo.
     *
     * Montado por `filtroDoEscopo` (ver `mesa/escopo.ts`) e aplicado sobre
     * qualquer outro filtro da tela.
     */
    escopo?: Record<string, unknown>;
    /**
     * Somente os chamados que o proprio usuario ABRIU — a visao do
     * solicitante. Independe de status: quem abriu quer ver tambem os que ja
     * foram encerrados.
     */
    somenteDoSolicitante?: boolean;
}

/**
 * Acesso ao modulo de Chamados.
 *
 * ESCOPO DELIBERADO, e a linha divisoria importa:
 *
 *  - LE tudo (chamados, mensagens, filas, categorias);
 *  - GRAVA apenas CONFIGURACAO (filas, categorias, calendario, canais, macros);
 *  - NAO grava OPERACAO. Abrir, responder, atribuir, transferir, escalar e
 *    mudar status pertencem ao `TicketServices` do MKTPlace2, que e o unico
 *    dono das transicoes de status e o mesmo ponto usado pelos canais
 *    WhatsApp e Telegram. Gravar operacao daqui criaria uma segunda regra de
 *    negocio, que divergiria da do canal na primeira mudanca.
 *
 * Todas as consultas vao pelo caminho Mongo NATIVO do findnative:
 * `type:'mongo'` + `useMongoManual:true` (ver BrokerHelper.findNativeSQL).
 * O `type:'mongodb'` NAO serve aqui — ele cai no tradutor SQL->Mongo.
 */
/*
 * `providedIn: 'root'`, e nao nos providers do modulo: o modal de abertura e
 * criado pelo NzModalService, que usa o injector do AMBIENTE — nao o do
 * modulo lazy nem o do componente que abriu. Registrado so no modulo, o
 * servico nao existia la e o modal quebrava com NG0201.
 *
 * Instancia unica nao incomoda: o servico nao guarda estado, so monta
 * requisicao.
 */
/**
 * Um atendimento onde se pode abrir chamado.
 *
 * Vem pronto do `TicketServices/openingOptions`: so as categorias que esta
 * pessoa pode escolher, mais os grupos do caminho ate elas, e so as filas
 * referenciadas por essas categorias.
 */
export interface AtendimentoDeAbertura {
    codecontract: number;
    nome: string;
    /**
     * A configuracao ENXUTA da central.
     *
     * `csatAtivo` e `reabrePrazoHoras` vem aqui porque as telas do solicitante
     * precisam deles e nao conseguem ler a configuracao sozinhas: ela e de
     * outro contrato e o filtro nativo de tenant recorta a consulta.
     */
    config: {
        prioridadePadrao?: TicketPriority,
        categoriaPadraoId?: string,
        csatAtivo?: boolean,
        reabrePrazoHoras?: number,
        intervaloAtualizacaoSegundos?: number,
    };
    categorias: TicketCategory[];
    filas: TicketQueue[];
}

/**
 * A leitura de CHAMADO e MENSAGEM passa pelo `TicketServices`?
 *
 * `true` (o padrao) usa `deskList`/`deskMessages`; `false` volta ao
 * `findnative`.
 *
 * POR QUE ISTO EXISTE: o chamado pertence ao CLIENTE (`codecontract`), porque
 * quem abriu precisa enxergar o proprio chamado. O atendente e da CENTRAL —
 * outro contrato. Pelo `findnative`, o filtro nativo de tenant recorta a
 * consulta pelos contratos de quem esta logado, e o atendente so via os
 * chamados dos clientes aos quais ele estivesse ligado na `MKTPUSERCOMPANY`.
 * Liga-lo a todos os contratos dos clientes resolveria a mesa e abriria para
 * ele TODAS as outras telas do sistema.
 *
 * Pelo servico, o recorte e o do modulo: o cadastro de atendentes decide quem
 * enxerga o que, e o filtro que a tela manda so consegue estreitar.
 *
 * A chave fica aqui para o dia da virada: sem os dois endpoints no ar, ponha
 * `false` e a mesa volta ao comportamento antigo.
 * Ver `docs/chamados/proposta-java-13-leitura-da-mesa.md`.
 */
const LEITURA_PELO_SERVICO = true;

@Injectable({ providedIn: 'root' })
export class ChamadosService {

    constructor(
        private http: HTTPService,
        private uas: UserAuthenticationServices
    ) { }

    // ──────────────────────── escopo de empresa ────────────────────────

    /**
     * Recorte de empresa aplicado a toda consulta.
     *
     * Equivale ao `companyFilter()` que a conciliadora usa no SQL: ADMIN ve
     * tudo, os demais veem apenas as empresas da propria lista.
     *
     * Isto e conveniencia de tela, NAO controle de acesso: qualquer um pode
     * chamar o findnative direto. A validacao que vale e a do TicketServices.
     */
    /**
     * Recorte de leitura da CONFIGURACAO.
     *
     * Por CONTRATO, e nao por empresa: o contrato e o cliente, e e ele que
     * contrata o atendimento. Uma fila, uma categoria ou um calendario
     * pertencem ao cliente — nao a uma unidade dele. Quando o mesmo cliente
     * tem varias empresas, a configuracao continua sendo uma so.
     *
     * O `codecompany` continua GRAVADO nos documentos por compatibilidade com
     * o Java, que ainda le a configuracao por empresa. Ver a nota em
     * `docs/chamados/java-proposto/README.md`.
     */
    private contractScope(codecontract?: number): Record<string, unknown> {

        // O contrato ESCOLHIDO manda. Antes este metodo devolvia todos os
        // contratos do usuario, entao trocar o seletor da aba Geral nao mudava
        // consulta nenhuma — a tela recarregava e mostrava exatamente a mesma
        // lista.
        const escolhido = codecontract ?? this.contratoDoRecorte();

        if (escolhido != null) {
            return { codecontract: Number(escolhido) };
        }

        // Sem escolha (usuario com varios contratos e nada selecionado): cai
        // no que ele pode ver.
        if (this.uas.user?.profile === 'ADMIN') {
            return {};
        }

        const contratos = this.contratosDoUsuario().map(item => item.codecontract);

        if (contratos.length === 0) {
            // Sem contrato nenhum o certo e nao ver nada — um filtro vazio
            // devolveria a colecao inteira, que e o oposto do pretendido.
            return { codecontract: { $in: [-1] } };
        }

        return { codecontract: { $in: contratos } };
    }

    /**
     * Recorte por empresa — usado so pelo que e da OPERACAO.
     *
     * O chamado continua tendo empresa e contrato: a empresa diz de qual
     * unidade e o atendimento, e a mesa filtra por ela quando precisa.
     */
    private companyScope(): Record<string, unknown> {

        if (this.uas.user?.profile === 'ADMIN') {
            return {};
        }

        const companies = (this.uas.user?.companylist ?? []).map(item => item.codecompany);

        if (companies.length === 0) {
            return { codecompany: { $in: [-1] } };
        }

        return { codecompany: { $in: companies } };
    }

    // ──────────────────────── consultas ────────────────────────

    /**
     * Lista da mesa de atendimento.
     *
     * `pageIndex` e zero-based porque o backend o repassa como `fetchSize`
     * (o skip do Mongo), e nao como numero de pagina.
     */
    listTickets(
        filter: TicketListFilter,
        pageIndex: number,
        pageSize: number,
        sort: TicketSort = { field: 'ULTIMA_MENSAGEM', desc: true }
    ): Observable<PagedResult<Ticket>> {

        const mongoFilter = this.buildTicketFilter(filter);

        if (LEITURA_PELO_SERVICO) {
            return this.deskList(mongoFilter, pageIndex, pageSize, this.buildSortDocument(sort));
        }

        return this.findNative<Ticket>(
            COLLECTION_TICKETS,
            mongoFilter,
            pageIndex,
            pageSize,
            this.buildSortDocument(sort)
        );
    }

    /**
     * A lista pelo `TicketServices`.
     *
     * O filtro continua sendo montado AQUI e vai no corpo: o servidor o
     * combina em `and` com o recorte dele, e `and` so estreita — nao ha como a
     * tela ampliar o proprio alcance. Duplicar a traducao de cada campo no
     * Java seria a segunda copia da mesma regra, e a que ficaria para tras.
     */
    private deskList(
        filtro: Record<string, unknown>,
        pageIndex: number,
        pageSize: number,
        ordem: Record<string, 1 | -1>
    ): Observable<PagedResult<Ticket>> {

        const body = {
            iduser: this.uas.user?.iduser,
            filtro: filtro,
            ordem: ordem,
            pagina: pageIndex,
            tamanho: pageSize > 0 ? pageSize : 50,
        };

        return this.http.postGeneric<any>(body, 'TicketServices/deskList', false, true).pipe(
            map(resposta => ({
                rows: (resposta?.['rows'] ?? []) as Ticket[],
                total: (resposta?.['total'] ?? 0) as number,
            }))
        );
    }

    /** Campo do documento correspondente a cada opcao de ordenacao. */
    private buildSortDocument(sort: TicketSort): Record<string, 1 | -1> {

        const direcao: 1 | -1 = sort.desc ? -1 : 1;

        switch (sort.field) {
            case 'ABERTURA':
                return { 'datas.abertura': direcao };
            case 'PRIORIDADE':
                // Pelo campo NUMERICO, nunca pelo texto: ordenar
                // 'BAIXA'|'NORMAL'|'ALTA'|'CRITICA' como texto sai em ordem
                // alfabetica. O desempate por ultima mensagem poe na frente,
                // entre dois criticos, o que esta parado ha mais tempo.
                return { prioridadeOrdem: direcao, 'ultimaMensagem.em': -1 };
            case 'PRAZO_SLA':
                // Prazo de RESPOSTA: e o compromisso mais proximo e o que
                // determina a ordem de trabalho de quem esta pegando fila.
                return { 'sla.prazoResposta': direcao };
            case 'ULTIMA_MENSAGEM':
            default:
                return { 'ultimaMensagem.em': direcao };
        }
    }

    /**
     * Somente o que mudou desde a ultima leitura — e o que sustenta o polling
     * da mesa sem recarregar a lista inteira.
     *
     * Usa o indice { codecompany: 1, modifiedOn: -1 }.
     */
    listTicketsChangedSince(filter: TicketListFilter, since: Date, maxRows: number): Observable<PagedResult<Ticket>> {

        const mongoFilter = this.buildTicketFilter(filter);
        mongoFilter['modifiedOn'] = { $gt: `ISODate('${since.toISOString()}')` };

        return this.findNative<Ticket>(
            COLLECTION_TICKETS,
            mongoFilter,
            0,
            maxRows,
            { modifiedOn: -1 }
        );
    }

    getTicketByNumero(numero: string): Observable<Ticket | null> {

        const mongoFilter: Record<string, unknown> = {
            ...this.companyScope(),
            numero: numero,
        };

        return this.findNative<Ticket>(COLLECTION_TICKETS, mongoFilter, 0, 1, null).pipe(
            map(result => result.rows.length > 0 ? result.rows[0] : null)
        );
    }

    /** Conversa do chamado, em ordem de chegada. */
    /**
     * A conversa de um chamado.
     *
     * Pelo servico tambem no caso do SOLICITANTE, e nao so da mesa: pelo
     * `findnative`, qualquer usuario do contrato do cliente lia as mensagens
     * de todos os chamados daquele contrato — inclusive as dos colegas. No
     * servico a autorizacao e por chamado.
     */
    listMessages(ticketId: string, pageIndex: number, pageSize: number): Observable<PagedResult<TicketMessage>> {

        if (LEITURA_PELO_SERVICO) {

            const body = {
                iduser: this.uas.user?.iduser,
                ticketId: ticketId,
                pagina: pageIndex,
                tamanho: pageSize > 0 ? pageSize : 200,
            };

            return this.http.postGeneric<any>(body, 'TicketServices/deskMessages', false, true).pipe(
                map(resposta => ({
                    rows: (resposta?.['rows'] ?? []) as TicketMessage[],
                    total: (resposta?.['total'] ?? 0) as number,
                }))
            );
        }

        return this.findNative<TicketMessage>(
            COLLECTION_MESSAGES,
            { ticketId: ticketId },
            pageIndex,
            pageSize,
            { seq: 1 }
        );
    }

    /**
     * `onlyActive` existe porque as telas querem coisas diferentes: a mesa e a
     * abertura so podem oferecer o que esta ativo, enquanto a administracao
     * precisa enxergar tambem o inativo — senao desativar uma fila a faz
     * sumir da propria tela que a desativou.
     */
    /**
     * @param codecontract recorte explicito. A abertura de chamado usa o
     * contrato que a PESSOA escolheu no modal, que pode nao ser o da
     * configuracao aberta em outra aba.
     */
    listQueues(onlyActive: boolean = true, codecontract?: number): Observable<TicketQueue[]> {

        const mongoFilter: Record<string, unknown> = { ...this.contractScope(codecontract) };
        if (onlyActive) {
            mongoFilter['active'] = true;
        }

        return this.findNative<TicketQueue>(COLLECTION_QUEUES, mongoFilter, 0, 500, { nome: 1 }).pipe(
            map(result => result.rows)
        );
    }

    /** @param codecontract recorte explicito — ver `listQueues`. */
    listCategories(onlyActive: boolean = true, codecontract?: number): Observable<TicketCategory[]> {

        const mongoFilter: Record<string, unknown> = { ...this.contractScope(codecontract) };
        if (onlyActive) {
            mongoFilter['active'] = true;
        }

        return this.findNative<TicketCategory>(COLLECTION_CATEGORIES, mongoFilter, 0, 500, { nome: 1 }).pipe(
            map(result => result.rows)
        );
    }

    // ──────────────────────── montagem do filtro ────────────────────────

    /**
     * Categorias oferecidas em um canal.
     *
     * O recorte por canal vai NA CONSULTA (`visivelEm.APP: true`), e nao numa
     * filtragem depois: e a segregacao de abertura pedida no projeto, e uma
     * categoria escondida do app nao deve nem trafegar ate a tela.
     *
     * O recorte por ACESSO (perfil/usuario/contrato) e que fica no cliente —
     * ele depende de quem esta logado, e o `acesso` e uma lista de objetos
     * dentro do documento.
     */
    listCategoriesForChannel(canal: TicketChannel): Observable<TicketCategory[]> {

        const mongoFilter: Record<string, unknown> = { ...this.contractScope() };
        mongoFilter['active'] = true;
        mongoFilter['visivelEm.' + canal] = true;

        return this.findNative<TicketCategory>(COLLECTION_CATEGORIES, mongoFilter, 0, 500, { nome: 1 }).pipe(
            map(result => result.rows)
        );
    }

    /** Perfil do usuario logado, usado no recorte de acesso das categorias. */
    perfilAtual(): string {
        return this.uas.user?.profile;
    }

    /** Contratos aos quais o usuario logado esta ligado. */
    contratosAtuais(): string[] {

        return (this.uas.user?.companylist ?? [])
            .map(item => item.codecontract)
            .filter(codigo => codigo != null)
            .map(codigo => String(codigo));
    }

    private buildTicketFilter(filter: TicketListFilter): Record<string, unknown> {

        const mongoFilter: Record<string, unknown> = { ...this.companyScope() };

        if (filter == null) {
            return mongoFilter;
        }

        // Empresa escolhida na tela e mais especifica que o escopo do usuario
        if (filter.codecompany != null) {
            mongoFilter['codecompany'] = filter.codecompany;
        }

        if (filter.status != null && filter.status.length > 0) {
            mongoFilter['status'] = { $in: filter.status };
        }

        if (filter.filaId != null) {
            mongoFilter['fila.id'] = filter.filaId;
        }

        if (filter.responsavelIduser != null) {
            mongoFilter['responsavel.iduser'] = filter.responsavelIduser;
        }

        if (filter.canal != null) {
            mongoFilter['origem.canal'] = filter.canal;
        }

        if (filter.codecontract != null) {
            mongoFilter['codecontract'] = filter.codecontract;
        }

        if (filter.codecontractConfiguracao != null) {
            mongoFilter['codecontractConfiguracao'] = filter.codecontractConfiguracao;
        }

        if (filter.numero != null && filter.numero.trim().length > 0) {
            mongoFilter['numero'] = filter.numero.trim();
        }

        if (filter.somenteDoSolicitante === true) {
            mongoFilter['origem.iduserSolicitante'] = this.uas.user?.iduser;
        }

        if (filter.texto != null && filter.texto.trim().length > 0) {
            mongoFilter['$text'] = { $search: filter.texto.trim() };
        }

        // O escopo do atendente entra POR ULTIMO e sem negociacao: e regra de
        // acesso, nao filtro de tela. Vem depois para que nenhum filtro
        // anterior possa relaxa-lo por engano.
        // O recorte de acesso do atendente NAO vai mais no filtro quando a
        // leitura passa pelo servico: quem decide o que cada um enxerga e o
        // `deskList`, pelo cadastro de atendentes.
        //
        // Mandar os dois quebrava a tela: quem nao tem cadastro de atendente
        // (todo solicitante) recebia `{_id: null}` do `filtroDoEscopo` — um
        // filtro que nao casa com nada de proposito — e ele entrava em `and`
        // com o recorte do servidor. Resultado: lista sempre vazia, inclusive
        // no "meus chamados".
        if (filter.escopo != null && !LEITURA_PELO_SERVICO) {
            Object.assign(mongoFilter, filter.escopo);
        }

        return mongoFilter;
    }

    /**
     * Usuarios que podem virar membro de uma fila da empresa.
     *
     * Consulta SQL SERVER, e nao Mongo — por isso NAO leva `type`/`dbName`:
     * o cadastro de usuario e do sistema inteiro (MKTPUSER) e o vinculo com a
     * empresa e MKTPUSERCOMPANY, o mesmo par usado pelo
     * `UserAuthenticationServices.getAllUsersCompany`.
     */
    /**
     * Usuarios de um CONTRATO.
     *
     * O caminho e MKTPUSER -> MKTPUSERCOMPANY -> MKTPCOMPANIES: o vinculo do
     * usuario e com a EMPRESA, e e a empresa que carrega o contrato. Nao ha
     * ligacao direta entre usuario e contrato para consultar.
     *
     * Sem contrato, cai na lista da empresa do recorte — e o comportamento
     * anterior, para contato que ainda nao tem cliente definido.
     */
    listContractUsers(codecontract: number): Observable<CompanyUser[]> {

        if (codecontract == null) {
            return this.listCompanyUsers(this.empresaDoRecorte());
        }

        // Coeracao para numero: o valor vem da tela, mas entra numa string de
        // SQL — deixar passar texto aqui seria abrir injecao.
        const contrato = Number(codecontract);

        if (!Number.isFinite(contrato)) {
            return of([]);
        }

        const sql = `SELECT U.IDUSER, U.USERNAME
                       FROM MKTPUSER U
                      WHERE U.ACTIVE = 'S'
                        AND EXISTS ( SELECT 1
                                       FROM MKTPUSERCOMPANY I
                                       JOIN MKTPCOMPANIES C ON C.CODECOMPANY = I.CODECOMPANY
                                      WHERE I.IDUSER = U.IDUSER
                                        AND C.CODECONTRACT = ${contrato} )
                      ORDER BY U.USERNAME`;

        const body = {
            entityName: 'Mktpuser',
            sql: sql,
        };

        return this.http.postGeneric<any>(body, 'JsonBrokerServices/findnative', false, true).pipe(
            map(response => (response?.['SQL'] ?? []) as CompanyUser[])
        );
    }

    /** Nomes ja resolvidos. Um chamado aberto duas vezes nao consulta duas. */
    private nomesDeUsuario = new Map<number, string>();

    /**
     * Nome de UM usuario pelo id.
     *
     * A mesa precisa dele para a variavel `{{solicitante}}` das respostas
     * prontas: quem abriu pelo app nem sempre deixa o nome no autor da
     * mensagem, e o marcador sairia cru na frente do cliente.
     *
     * Consulta direta ao MKTPUSER, sem recorte de empresa: o solicitante e
     * quase sempre de outro contrato que o atendente — filtrar aqui devolveria
     * vazio justamente no caso que importa.
     */
    nomeDeUsuario(iduser: number): Observable<string> {

        const id = Number(iduser);

        if (!Number.isFinite(id)) {
            return of(null);
        }

        if (this.nomesDeUsuario.has(id)) {
            return of(this.nomesDeUsuario.get(id));
        }

        const body = {
            entityName: 'Mktpuser',
            // Coeracao para numero antes de entrar na string de SQL.
            sql: `SELECT U.USERNAME FROM MKTPUSER U WHERE U.IDUSER = ${id}`,
        };

        return this.http.postGeneric<any>(body, 'JsonBrokerServices/findnative', false, true).pipe(
            map(resposta => {
                const linhas = (resposta?.['SQL'] ?? []) as { USERNAME?: string }[];
                const nome = linhas.length > 0 ? linhas[0].USERNAME : null;
                this.nomesDeUsuario.set(id, nome);
                return nome;
            })
        );
    }

    listCompanyUsers(codecompany: number): Observable<CompanyUser[]> {

        // Coeracao para numero: o valor vem da sessao, mas ele entra numa
        // string de SQL — deixar passar texto aqui seria abrir injecao.
        const empresa = Number(codecompany);

        if (!Number.isFinite(empresa)) {
            return of([]);
        }

        const sql = `SELECT U.IDUSER, U.USERNAME
                       FROM MKTPUSER U
                      WHERE U.ACTIVE = 'S'
                        AND EXISTS ( SELECT 1 FROM MKTPUSERCOMPANY I
                                      WHERE I.IDUSER = U.IDUSER
                                        AND I.CODECOMPANY = ${empresa} )
                      ORDER BY U.USERNAME`;

        const body = {
            entityName: 'Mktpuser',
            sql: sql,
        };

        return this.http.postGeneric<any>(body, 'JsonBrokerServices/findnative', false, true).pipe(
            map(response => (response?.['SQL'] ?? []) as CompanyUser[])
        );
    }

    /**
     * Configuracao da empresa.
     *
     * Le o documento de nivel EMPRESA (`codecontract: null`), que e o que vale
     * para todos os contratos.
     */
    /**
     * A configuracao de um CONTRATO.
     *
     * A chave e o contrato. A empresa saiu da consulta: cliente com duas
     * unidades tinha duas configuracoes que precisavam ser mantidas iguais na
     * mao, e bastava esquecer uma para o mesmo cliente atender de dois jeitos.
     */
    getConfig(codecontract: number): Observable<TicketConfig | null> {

        const mongoFilter: Record<string, unknown> = { codecontract: codecontract };

        return this.findNative<TicketConfig>(COLLECTION_CONFIG, mongoFilter, 0, 5, null).pipe(
            map(result => result.rows.length > 0 ? result.rows[0] : null)
        );
    }

    /** Configuracoes que o usuario enxerga — uma por contrato. */
    /**
     * O que a pessoa pode escolher ao ABRIR um chamado.
     *
     * PELO JAVA, e nao pelo `findnative`, por dois motivos:
     *
     *  1. O service desk e cross-contrato. Quem abre pertence ao contrato
     *     dele; a central de atendimento e de outro. O filtro nativo de tenant
     *     acrescenta `codecontract IN (contratos do usuario)` a toda consulta
     *     do `findnative`, entao a configuracao da central nunca chegaria
     *     aqui — nao ha o que o front possa mandar para contornar isso.
     *  2. O recorte de `acesso` da categoria agora e decidido no servidor.
     *     Aqui ele so escondia.
     *
     * Ver `docs/chamados/proposta-java-8-abertura-entre-contratos.md`.
     */
    opcoesDeAbertura(): Observable<AtendimentoDeAbertura[]> {

        const body = { iduser: this.uas.user?.iduser};

        return this.http.postGeneric<any>(body, 'TicketServices/openingOptions', false, true).pipe(
            map(resposta => (resposta?.['atendimentos'] ?? []) as AtendimentoDeAbertura[])
        );
    }

    listConfigs(): Observable<TicketConfig[]> {

        return this.findNative<TicketConfig>(COLLECTION_CONFIG, this.contractScope(), 0, 100, null).pipe(
            map(result => result.rows)
        );
    }


    saveConfig(config: TicketConfig): Observable<unknown> {
        return this.saveDocument(COLLECTION_CONFIG, config);
    }

    /**
     * Agentes disponiveis para o bot de triagem.
     *
     * SEM `dbName`: `Mktpagents` mora no banco padrao, e nao no banco dedicado
     * dos chamados. Mandar o dbName aqui procuraria a colecao no lugar errado
     * e devolveria lista vazia, sem erro nenhum.
     */
    listBotAgents(): Observable<BotAgent[]> {

        const body = {
            entityName: 'Mktpagents',
            useMongoManual: true,
            type: 'mongo',
            maxRows: 200,
            sql: StringUtils.stringifyMongoCondition({}),
            sort: { agentName: 1 },
        };

        return this.http.postGeneric<any>(body, 'JsonBrokerServices/findnative', false, true).pipe(
            map(response => (response?.['SQL'] ?? []) as BotAgent[])
        );
    }

    // ──────────────────────── canais ────────────────────────

    listChannels(onlyActive: boolean = true): Observable<TicketChannelConfig[]> {

        const mongoFilter: Record<string, unknown> = { ...this.contractScope() };
        if (onlyActive) {
            mongoFilter['active'] = true;
        }

        return this.findNative<TicketChannelConfig>(COLLECTION_CHANNELS, mongoFilter, 0, 200, { nome: 1 }).pipe(
            map(result => result.rows)
        );
    }

    saveChannel(canal: TicketChannelConfig): Observable<unknown> {
        return this.saveDocument(COLLECTION_CHANNELS, canal);
    }

    removeChannel(id: string): Observable<unknown> {
        return this.removeDocument(COLLECTION_CHANNELS, id);
    }

    /**
     * Fluxos TemAPI ativos da empresa, para escolher o caminho de envio.
     *
     * Consulta SQL SERVER, sem `type`/`dbName`: `MKTPCFG` nao esta no Mongo.
     */
    listFlows(): Observable<FlowOption[]> {

        const empresas = (this.uas.user?.companylist ?? []).map(item => Number(item.codecompany));
        const ehAdmin = this.uas.user?.profile === 'ADMIN';

        // Coeracao para numero antes de entrar na string de SQL — os valores
        // vem da sessao, mas texto aqui seria injecao.
        const listaEmpresas = empresas.filter(codigo => Number.isFinite(codigo)).join(',');

        const recorte = ehAdmin || listaEmpresas.length === 0
            ? '0=0'
            : `EXISTS ( SELECT 1 FROM MKTPCFGCOMPANY AA WHERE A.IDCONF = AA.IDCONF AND AA.CODECOMPANY IN (${listaEmpresas}) )`;

        const sql = `SELECT A.IDCONF, A.NAME
                       FROM MKTPCFG A (NOLOCK)
                      WHERE A.ACTIVE = 'S'
                        AND ${recorte}
                      ORDER BY A.NAME`;

        return this.http.postGeneric<any>({ entityName: 'Mktpcfg', sql: sql }, 'JsonBrokerServices/findnative', false, true).pipe(
            map(response => (response?.['SQL'] ?? []) as FlowOption[])
        );
    }

    // ──────────────────────── atendentes ────────────────────────

    listAgents(onlyActive: boolean = true): Observable<TicketAgent[]> {

        const mongoFilter: Record<string, unknown> = { ...this.contractScope() };
        if (onlyActive) {
            mongoFilter['active'] = true;
        }

        return this.findNative<TicketAgent>(COLLECTION_AGENTS, mongoFilter, 0, 500, { nome: 1 }).pipe(
            map(result => result.rows)
        );
    }

    /**
     * O cadastro do usuario logado.
     *
     * E ele que decide o que a mesa mostra. Sem cadastro, o escopo devolve
     * "nao ve nada" — ver `mesa/escopo.ts`.
     */
    getAgenteAtual(): Observable<TicketAgent | null> {

        const iduser = this.uas.user?.iduser;

        if (iduser == null) {
            return of(null);
        }

        const mongoFilter: Record<string, unknown> = {
            ...this.contractScope(),
            iduser: iduser,
        };

        return this.findNative<TicketAgent>(COLLECTION_AGENTS, mongoFilter, 0, 1, null).pipe(
            map(result => result.rows.length > 0 ? result.rows[0] : null)
        );
    }

    saveAgent(agente: TicketAgent): Observable<unknown> {
        return this.saveDocument(COLLECTION_AGENTS, agente);
    }

    removeAgent(id: string): Observable<unknown> {
        return this.removeDocument(COLLECTION_AGENTS, id);
    }

    /**
     * Reescreve o `membros` das filas a partir do cadastro de atendentes.
     *
     * PROJECAO, nao segunda fonte de verdade: o Java le
     * `fila.membros[papel=SUPERVISOR]` para notificar escalonamento, e nao
     * conhece a colecao de atendentes. Sem esta sincronizacao, cadastrar um
     * supervisor novo nao faria diferenca nenhuma nas notificacoes.
     *
     * Roda sobre TODAS as filas: e a unica forma de tambem REMOVER quem saiu.
     */
    sincronizarMembrosDasFilas(): Observable<unknown> {

        return forkJoin({
            agentes: this.listAgents(true),
            filas: this.listQueues(false),
        }).pipe(
            switchMap(({ agentes, filas }) => {

                const gravacoes: Observable<unknown>[] = [];

                for (const fila of filas) {

                    const membros = agentes
                        .filter(agente => (agente.filas ?? []).some(item => item.id === fila._id))
                        .map(agente => ({
                            iduser: agente.iduser,
                            nome: agente.nome,
                            // GERENTE vira SUPERVISOR na projecao: o Java so
                            // conhece dois papeis, e gerente notificado como
                            // supervisor e o comportamento desejado.
                            papel: agente.papel === 'GERENTE' ? 'SUPERVISOR' : agente.papel,
                            recebeAuto: agente.recebeAuto === true,
                        })) as TicketQueueMember[];

                    // So grava o que mudou: reescrever fila igual gera trafego
                    // e um modifiedOn novo sem motivo.
                    if (JSON.stringify(fila.membros ?? []) === JSON.stringify(membros)) {
                        continue;
                    }

                    fila.membros = membros;
                    gravacoes.push(this.saveDocument(COLLECTION_QUEUES, fila));
                }

                return gravacoes.length > 0 ? forkJoin(gravacoes) : of([]);
            })
        );
    }

    // ──────────────────────── calendarios ────────────────────────

    listCalendars(onlyActive: boolean = true): Observable<TicketCalendar[]> {

        const mongoFilter: Record<string, unknown> = { ...this.contractScope() };
        if (onlyActive) {
            mongoFilter['active'] = true;
        }

        return this.findNative<TicketCalendar>(COLLECTION_CALENDARS, mongoFilter, 0, 200, { nome: 1 }).pipe(
            map(result => result.rows)
        );
    }

    saveCalendar(calendario: TicketCalendar): Observable<unknown> {
        return this.saveDocument(COLLECTION_CALENDARS, calendario);
    }

    removeCalendar(id: string): Observable<unknown> {
        return this.removeDocument(COLLECTION_CALENDARS, id);
    }

    // ──────────────────────── respostas prontas ────────────────────────

    listMacros(onlyActive: boolean = true): Observable<TicketMacro[]> {

        const mongoFilter: Record<string, unknown> = { ...this.contractScope() };
        if (onlyActive) {
            mongoFilter['active'] = true;
        }

        return this.findNative<TicketMacro>(COLLECTION_MACROS, mongoFilter, 0, 500, { titulo: 1 }).pipe(
            map(result => result.rows)
        );
    }

    saveMacro(macro: TicketMacro): Observable<unknown> {
        return this.saveDocument(COLLECTION_MACROS, macro);
    }

    removeMacro(id: string): Observable<unknown> {
        return this.removeDocument(COLLECTION_MACROS, id);
    }

    // ──────────────────────── contatos ────────────────────────

    /**
     * Contatos externos.
     *
     * A lista cresce sozinha — todo numero que escreve pela primeira vez vira
     * um contato — entao os filtros nao sao conveniencia: sem eles a tela fica
     * inutil em poucas semanas de operacao.
     */
    listContacts(canal: string, texto: string, pageIndex: number, pageSize: number): Observable<PagedResult<TicketContact>> {

        const mongoFilter: Record<string, unknown> = { ...this.contractScope() };

        // Os dois formatos de canal convivem: o contato passou a ter uma LISTA
        // (`canais`), mas documento antigo tem `canal`/`identificador` soltos.
        // Enquanto a migracao nao rodar, ignorar o formato antigo faria o
        // contato sumir da tela — e ele continua atendendo normalmente.
        const condicoes: Record<string, unknown>[] = [];

        if (canal != null && canal.trim().length > 0) {
            condicoes.push({
                $or: [
                    { 'canais.canal': canal },
                    { canal: canal },
                ],
            });
        }

        if (texto != null && texto.trim().length > 0) {
            // Sem indice de texto nesta colecao: a busca e por trecho do
            // identificador, do nome ou do e-mail, com regex. Aceitavel porque
            // o filtro sempre vem junto do recorte de empresa.
            const alvo = this.escaparRegex(texto.trim());
            condicoes.push({
                $or: [
                    { 'canais.identificador': { $regex: alvo, $options: 'i' } },
                    { identificador: { $regex: alvo, $options: 'i' } },
                    { nome: { $regex: alvo, $options: 'i' } },
                    { email: { $regex: alvo, $options: 'i' } },
                ],
            });
        }

        // `$and` explicito: os dois filtros usam `$or`, e um sobrescreveria o
        // outro se fossem chaves soltas do mesmo objeto.
        if (condicoes.length > 0) {
            mongoFilter['$and'] = condicoes;
        }

        return this.findNative<TicketContact>(
            COLLECTION_CONTACTS, mongoFilter, pageIndex, pageSize, { nome: 1 });
    }

    saveContact(contato: TicketContact): Observable<unknown> {
        return this.saveDocument(COLLECTION_CONTACTS, contato);
    }

    removeContact(id: string): Observable<unknown> {
        return this.removeDocument(COLLECTION_CONTACTS, id);
    }

    /**
     * Escapa o que o usuario digitou antes de virar regex.
     *
     * Sem isso, um `+` colado de um numero de telefone e um quantificador
     * invalido e a consulta estoura — e `.*` digitado por engano viraria uma
     * varredura da colecao inteira.
     */
    private escaparRegex(texto: string): string {
        return texto.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    // ──────────────────────── escrita de CONFIGURACAO ────────────────────────

    saveQueue(queue: TicketQueue): Observable<unknown> {
        return this.saveDocument(COLLECTION_QUEUES, queue);
    }

    removeQueue(id: string): Observable<unknown> {
        return this.removeDocument(COLLECTION_QUEUES, id);
    }

    saveCategory(category: TicketCategory): Observable<unknown> {
        return this.saveDocument(COLLECTION_CATEGORIES, category);
    }

    removeCategory(id: string): Observable<unknown> {
        return this.removeDocument(COLLECTION_CATEGORIES, id);
    }

    /**
     * Grava um documento de configuracao.
     *
     * `dbName` e obrigatorio aqui: o modulo vive num banco dedicado, e sem ele
     * o BrokerHelper grava no banco PADRAO — sem erro nenhum. O sintoma seria
     * o pior possivel: a tela le do temapi_chamados (findnative respeita
     * dbName) e continua vazia, entao o cadastro "some" depois de salvo.
     *
     * Depende do saveValue() honrar dbName — ver docs/chamados/proposta-java.md.
     */
    private saveDocument(entityName: string, document: object): Observable<unknown> {

        // `object` na assinatura e a leitura por indice aqui: uma interface
        // declarada (TicketQueue, TicketCategory) nao e atribuivel a
        // Record<string, unknown> porque nao tem index signature.
        const campos = document as Record<string, unknown>;

        const body: Record<string, unknown> = {
            entityName: entityName.toLocaleLowerCase(),
            useMongo: true,
            dbName: CHAMADOS_DB_NAME,
            ...campos,
        };

        // _id vazio = documento novo. Enviar `_id: null` faria o backend tentar
        // casar com um documento de _id nulo em vez de inserir.
        if (campos['_id'] == null || `${campos['_id']}`.trim().length === 0) {
            delete body['_id'];
        } else {
            body['condition'] = `_id=toobjectid('${campos['_id']}')`;
        }

        return this.http.postGeneric<unknown>(body, 'JsonBrokerServices/save', false, true);
    }

    private removeDocument(entityName: string, id: string): Observable<unknown> {

        const body: Record<string, unknown> = {
            entityName: entityName.toLocaleLowerCase(),
            useMongo: true,
            dbName: CHAMADOS_DB_NAME,
            condition: `_id=toobjectid('${id}')`,
        };

        return this.http.postGeneric<unknown>(body, 'JsonBrokerServices/remove', false, true);
    }

    // ════════════════════════ recorte da configuracao ════════════════════════

    /*
     * CONTRATO primeiro, EMPRESA depois.
     *
     * E a hierarquia real da TemAPI: o contrato e o cliente, e a empresa e uma
     * unidade dentro dele. Escolher a empresa antes obrigaria a listar empresa
     * de todos os contratos misturadas — e nomes de empresa se repetem entre
     * contratos.
     *
     * O recorte fica no SERVICO, e nao na aba Geral, porque as outras telas
     * dependem dele: a lista de membros da fila e a de usuarios do contato
     * saem do MKTPUSER daquela empresa. Antes elas usavam a empresa PADRAO do
     * login, o que dava a lista errada assim que a configuracao passou a
     * poder ser de outra empresa.
     */

    /** Contrato escolhido. null = nao escolhido ainda. */
    private contratoAtual: number = null;

    private readonly chaveDoRecorte = 'tkt.recorte';

    /** Contratos que o usuario enxerga, do `companylist`. */
    contratosDoUsuario(): { codecontract: number, nome: string }[] {

        const vistos = new Map<number, string>();

        for (const item of this.uas.user?.companylist ?? []) {

            if (item.codecontract == null) {
                continue;
            }

            const codigo = Number(item.codecontract);

            if (Number.isFinite(codigo) && !vistos.has(codigo)) {
                vistos.set(codigo, item.contractname ?? String(codigo));
            }
        }

        return Array.from(vistos.entries()).map(([codigo, nome]) => ({ codecontract: codigo, nome: nome })).sort((a, b) => a.codecontract - b.codecontract);
    }

    /**
     * Empresas de um contrato.
     *
     * Sem contrato informado, devolve todas as do usuario: e o caso de quem so
     * tem uma empresa e nunca viu a tela de contrato.
     */
    empresasDoContrato(codecontract: number): { codecompany: number, nome: string }[] {

        const vistas = new Map<number, string>();

        for (const item of this.uas.user?.companylist ?? []) {

            if (codecontract != null && Number(item.codecontract) !== Number(codecontract)) {
                continue;
            }

            const codigo = Number(item.codecompany);

            if (Number.isFinite(codigo) && !vistas.has(codigo)) {
                vistas.set(codigo, item.companyname);
            }
        }

        return Array.from(vistas.entries()).map(([codigo, nome]) => ({ codecompany: codigo, nome: nome })).sort((a, b) => a.codecompany - b.codecompany);
    }

    /**
     * `2 - Cliente X` — o codigo na frente do nome.
     *
     * O codigo e o que aparece no resto do sistema (log, erro do Java,
     * consulta no banco) e o que desempata nome repetido.
     */
    rotuloDoContrato(codecontract: number): string {

        if (codecontract == null) {
            return '';
        }

        const nome = this.nomeDoContrato(codecontract);

        return nome === String(codecontract) ? nome : codecontract + ' - ' + nome;
    }

    /** Nome do contrato, para a mesa e para os cabecalhos. */
    nomeDoContrato(codecontract: number): string {

        if (codecontract == null) {
            return '';
        }

        const encontrado = this.contratosDoUsuario()
            .find(contrato => contrato.codecontract === Number(codecontract));

        // Contrato fora da lista do usuario ainda mostra o numero: melhor um
        // codigo que da para investigar do que uma celula em branco.
        return encontrado != null ? encontrado.nome : String(codecontract);
    }

    /**
     * Avisa que o recorte mudou.
     *
     * As outras abas de configuracao ja estao montadas quando alguem troca o
     * contrato na aba Geral — sem este aviso, elas continuariam mostrando os
     * dados do cliente anterior ate a pessoa sair e voltar.
     */
    readonly recorteMudou = new Subject<void>();

    /**
     * @param codecontract o cliente. A configuracao nao tem empresa: fila,
     * categoria e calendario valem para o contrato inteiro.
     */
    definirRecorte(codecontract: number): void {

        this.contratoAtual = codecontract;

        try {
            localStorage.setItem(this.chaveDoRecorte, JSON.stringify({
                codecontract: codecontract,
            }));
        } catch (erro) {
            // Sem armazenamento, o recorte vale so para esta sessao de tela.
        }

        this.recorteMudou.next();
    }

    /**
     * Empresa do recorte, com o padrao do login como ultimo recurso.
     *
     * TODA tela de configuracao le por aqui: e o que garante que a fila, o
     * contato e a categoria pertencam a mesma empresa que a aba Geral esta
     * configurando.
     */
    empresaDoRecorte(): number {

        // DERIVADA do contrato, nunca escolhida: a configuracao nao tem
        // empresa. Isto existe so para o punhado de consultas que ainda
        // precisam de uma — a de usuarios do SQL Server, por exemplo.
        const empresas = this.empresasDoContrato(this.contratoDoRecorte());

        if (empresas.length > 0) {
            return empresas[0].codecompany;
        }

        return this.defaultCodeCompany();
    }

    contratoDoRecorte(): number {

        if (this.contratoAtual != null) {
            return this.contratoAtual;
        }

        const salvo = this.lerRecorteSalvo();

        if (salvo?.codecontract != null) {
            this.contratoAtual = salvo.codecontract;
            return this.contratoAtual;
        }

        return this.contratoDoUsuario();
    }

    /**
     * Contrato do proprio usuario logado.
     *
     * E o que carimba o chamado aberto pelo app: quem abre pertence a um
     * contrato, e o atendimento precisa saber de qual cliente e o chamado.
     * Com mais de um, nao ha o que adivinhar — fica nulo e o backend resolve
     * pela configuracao.
     */
    contratoDoUsuario(): number {

        const contratos = this.contratosDoUsuario();

        return contratos.length === 1 ? contratos[0].codecontract : null;
    }

    private lerRecorteSalvo(): { codecontract: number } {

        try {
            const bruto = localStorage.getItem(this.chaveDoRecorte);
            return bruto != null ? JSON.parse(bruto) : null;

        } catch (erro) {
            return null;
        }
    }

    /** Usuario logado — a mesa usa para saber se o chamado ja e dele. */
    idUsuarioAtual(): number {
        return this.uas.user?.iduser;
    }

    /** Empresa do usuario, usada como padrao ao criar configuracao nova. */
    defaultCodeCompany(): number {
        return this.uas.user?.codecompany;
    }

    // ══════════════════════════════════════════════════════════════
    //  OPERACAO — via TicketServices (Java)
    // ══════════════════════════════════════════════════════════════

    /*
     * Abrir, responder e mudar status NAO gravam daqui: passam pelo
     * `TicketServices`, que e o unico dono das transicoes e o mesmo ponto
     * usado pelos canais WhatsApp e Telegram. E o que impede a tela e o canal
     * divergirem na primeira alteracao de regra.
     */

    abrirChamado(dados: AbrirChamadoRequest): Observable<{ ticketId: string, numero: string }> {

        const body = {
            ...dados,
            // `codecontractConfiguracao` e a CENTRAL onde o chamado esta
            // sendo aberto, sempre escolhida na tela. `codecontract` e o
            // CLIENTE: o padrao do login so vale para quem tem um contrato so
            // — com mais de um, quem abre e perguntado, porque adivinhar joga
            // o chamado na conta do cliente errado.
            codecontractConfiguracao: dados.codecontractConfiguracao,
            codecontract: dados.codecontract ?? this.contratoDoUsuario() ,
            codecompany: dados.codecompany ?? this.defaultCodeCompany(),
            iduser: this.uas.user?.iduser,
        };

        return this.http.postGeneric<any>(body, 'TicketServices/open', false, true);
    }

    /**
     * O link para baixar um anexo.
     *
     * O `ref` guarda a CHAVE do objeto, e nao uma URL: anexo de chamado e dado
     * de cliente, e uma URL publica vale para sempre para quem a tiver. O
     * servidor assina um link de poucos minutos e so entrega a quem pode ver
     * AQUELE chamado — a mesma checagem da conversa.
     */
    urlDoAnexo(ticketId: string, ref: string): Observable<string> {

        const body = {
            iduser: this.uas.user?.iduser,
            ticketId: ticketId,
            ref: ref,
        };

        return this.http.postGeneric<any>(body, 'TicketServices/anexoUrl', false, true).pipe(
            map(resposta => (resposta?.['resultado'] ?? null) as string)
        );
    }

    responder(ticket: Ticket, texto: string, interna: boolean, templateName?: string,
        anexos?: TicketAttachment[]): Observable<unknown> {

        const body: Record<string, unknown> = {
            ticketId: ticket._id,
            iduser: this.uas.user?.iduser,
            texto: texto,
            interna: interna,
            modifiedOn: this.versaoDoChamado(ticket),
        };

        if (anexos != null && anexos.length > 0) {
            body['anexos'] = anexos;
        }

        if (templateName != null && templateName.trim().length > 0) {
            body['templateName'] = templateName.trim();
        }

        return this.http.postGeneric<unknown>(body, 'TicketServices/reply', false, true);
    }

    /**
     * Mensagem de quem ABRIU o chamado, pelo app.
     *
     * Porta separada do `responder` de proposito. O `reply` e o caminho de
     * quem atende: grava `autor.tipo = ATENDENTE` fixo, carimba a primeira
     * resposta do SLA e joga o status para AGUARDANDO_CLIENTE. O `appReply`
     * entra pelo funil do inbox, como as mensagens do WhatsApp e do Telegram,
     * e o backend confere que o chamado e mesmo de quem esta escrevendo.
     */
    responderComoSolicitante(ticket: Ticket, texto: string,
        anexos?: TicketAttachment[]): Observable<unknown> {

        const body: Record<string, unknown> = {
            ticketId: ticket._id,
            iduser: this.uas.user?.iduser,
            texto: texto,
            modifiedOn: this.versaoDoChamado(ticket),
        };

        if (anexos != null && anexos.length > 0) {
            body['anexos'] = anexos;
        }

        return this.http.postGeneric<unknown>(body, 'TicketServices/appReply', false, true);
    }

    assumir(ticket: Ticket): Observable<unknown> {

        const body = {
            ticketId: ticket._id,
            iduser: this.uas.user?.iduser,
            modifiedOn: this.versaoDoChamado(ticket),
        };

        return this.http.postGeneric<unknown>(body, 'TicketServices/assign', false, true);
    }

    transferir(ticket: Ticket, filaId: string, iduserDestino: number, motivo: string): Observable<unknown> {

        const body: Record<string, unknown> = {
            ticketId: ticket._id,
            iduser: this.uas.user?.iduser,
            motivo: motivo,
            modifiedOn: this.versaoDoChamado(ticket),
        };

        // Um OU outro: o Java exige exatamente um dos dois e recusa os dois
        // vazios. Mandar os dois deixaria a decisao para o backend, que nao e
        // quem sabe o que a pessoa escolheu na tela.
        if (filaId != null) {
            body['filaId'] = filaId;
        } else if (iduserDestino != null) {
            body['iduserDestino'] = iduserDestino;
        }

        return this.http.postGeneric<unknown>(body, 'TicketServices/transfer', false, true);
    }

    escalar(ticket: Ticket, motivo: string): Observable<unknown> {

        const body = {
            ticketId: ticket._id,
            iduser: this.uas.user?.iduser,
            motivo: motivo,
            modifiedOn: this.versaoDoChamado(ticket),
        };

        return this.http.postGeneric<unknown>(body, 'TicketServices/escalate', false, true);
    }

    /**
     * @param solucao o que foi feito. So no RESOLVIDO — e o texto que fica no
     * campo `solucao` do chamado, separado da conversa.
     */
    mudarStatus(ticket: Ticket, status: TicketStatus, motivo: string,
        solucao?: string, comandoExecutado?: string): Observable<unknown> {

        const body: Record<string, unknown> = {
            ticketId: ticket._id,
            iduser: this.uas.user?.iduser,
            status: status,
            motivo: motivo,
            modifiedOn: this.versaoDoChamado(ticket),
        };

        if (solucao != null && solucao.trim().length > 0) {
            body['solucao'] = solucao.trim();
        }

        if (comandoExecutado != null && comandoExecutado.trim().length > 0) {
            body['comandoExecutado'] = comandoExecutado.trim();
        }

        return this.http.postGeneric<unknown>(body, 'TicketServices/status', false, true);
    }

    /**
     * Tira o chamado do bot e devolve para a fila humana.
     *
     * O mesmo endpoint que o proprio assistente chama quando decide que nao da
     * conta — por isso nao leva `modifiedOn`: quando a mesa aperta o botao, o
     * bot pode ter acabado de escrever, e recusar por conflito seria prender o
     * chamado no assistente justamente na hora em que alguem quer resgata-lo.
     */
    tirarDoBot(ticket: Ticket, motivo: string): Observable<unknown> {

        const body = {
            ticketId: ticket._id,
            motivo: motivo,
        };

        return this.http.postGeneric<unknown>(body, 'TicketServices/handoff', false, true);
    }

    avaliar(ticket: Ticket, nota: number, comentario: string): Observable<unknown> {

        const body = {
            ticketId: ticket._id,
            nota: nota,
            comentario: comentario,
        };

        return this.http.postGeneric<unknown>(body, 'TicketServices/csat', false, true);
    }

    /**
     * Versao do chamado para a checagem de concorrencia do backend.
     *
     * Enviada em MILISSEGUNDOS, e nao em ISO, de proposito. O
     * `garantirVersao` aceita as duas formas, mas a ISO vem do
     * `Instant.toString()` do Java, que OMITE os milissegundos quando eles sao
     * zero (`...:44Z` em vez de `...:44.000Z`) — enquanto o `toISOString()` do
     * JavaScript sempre os escreve. Nesse caso as duas strings diferem, o
     * backend conclui que alguem alterou o chamado e recusa uma gravacao
     * legitima. Em milissegundos nao ha ambiguidade de formato.
     */
    private versaoDoChamado(ticket: Ticket): string {

        const data = this.paraData(ticket?.modifiedOn);

        // Sem versao conhecida, o backend pula a checagem. E o certo: chamado
        // recem-lido de um caminho que nao trouxe o campo nao deve travar.
        return data != null ? String(data.getTime()) : null;
    }

    /** Data do backend: string ISO, `{ $date }` ou Date. */
    private paraData(valor: unknown): Date {

        if (valor == null) {
            return null;
        }

        if (valor instanceof Date) {
            return valor;
        }

        if (typeof valor === 'string') {
            const data = new Date(valor);
            return isNaN(data.getTime()) ? null : data;
        }

        if (typeof valor === 'object') {
            const comDate = valor as { $date?: string | number };
            if (comDate.$date != null) {
                const data = new Date(comDate.$date);
                return isNaN(data.getTime()) ? null : data;
            }
        }

        return null;
    }

    // ──────────────────────── acesso ao backend ────────────────────────

    /**
     * Consulta nativa no Mongo.
     *
     * `stringifyMongoCondition` converte os marcadores ObjectId('...') e
     * ISODate('...') — sem ele a data viraria texto entre aspas e nunca
     * casaria com um campo de data.
     */

    private findNative<T>(
        collection: string,
        filter: Record<string, unknown>,
        pageIndex: number,
        pageSize: number,
        sort: Record<string, 1 | -1> | null
    ): Observable<PagedResult<T>> {

        const body: Record<string, unknown> = {
            entityName: collection.toLocaleLowerCase(),
            useMongoManual: true,
            type: 'mongo',
            dbName: CHAMADOS_DB_NAME,
            sql: StringUtils.stringifyMongoCondition(filter),
            fetchSize: pageIndex,
            // nunca zero: zerado o backend assume 20 linhas
            maxRows: pageSize > 0 ? pageSize : 50,
            sort: sort,
        };

        return this.http.postGeneric<any>(body, 'JsonBrokerServices/findnative', false, true).pipe(
            map(response => ({
                rows: (response?.['SQL'] ?? []) as T[],
                total: (response?.['totalLines'] ?? 0) as number,
            }))
        );
    }
}
